import React, { useEffect, useState, useRef } from "react"
import { useSelector } from "react-redux"
import { usePermissionsList } from "../../../hooks/queries/usePermissions"
import Popup from "reactjs-popup"
import { usePostHog } from "posthog-js/react"
import { FaUser, FaClock, FaStethoscope, FaChevronDown } from "react-icons/fa"
import EditPatient from "../../NurseAssignments/EditPatient"
import { getScheduleSelection } from "../../DailyCalendarFiles/CustomCalendar/scheduleSelection"
import { convertMinutesToHoursAndMinutesString } from "../../../utils/generalUtils"

const UnassignedPatient = ({ patient, openEditPopup }) => {
	const getAcuityColor = acuity => {
		const acuityNum = Number(acuity)
		if (acuityNum >= 4) {
			return "text-red-700 bg-red-200 border-red-300" // High acuity (4-5)
		} else if (acuityNum === 3) {
			return "text-orange-700 bg-orange-200 border-orange-300" // Medium-high acuity
		} else if (acuityNum === 2) {
			return "text-yellow-700 bg-yellow-200 border-yellow-300" // Medium acuity
		} else if (acuityNum === 1) {
			return "text-green-700 bg-green-200 border-green-300" // Low acuity
		} else {
			return "text-gray-700 bg-gray-200 border-gray-300" // Unknown/invalid
		}
	}

	return (
		<div
			className="relative mt-4 p-5 rounded-xl cursor-pointer border-2 transition-all duration-300 bg-white border-gray-200 hover:border-customBlue hover:shadow-md"
			onClick={() => openEditPopup(patient)}
		>
			<div className="flex items-start justify-between">
				<div className="flex-1">
					<div className="flex items-center mb-3">
						<FaUser className="text-customBlue mr-2" />
						<h3 className="font-semibold text-lg text-gray-800">
							{patient.patientName}
						</h3>
					</div>

					<div className="space-y-2">
						<div className="flex items-center text-sm text-gray-600">
							<FaStethoscope className="mr-2 text-customBlue" />
							<span className="font-medium">Treatment:</span>
							<span className="ml-1">{patient.infusionType}</span>
						</div>

						<div className="flex items-center text-sm text-gray-600">
							<FaClock className="mr-2 text-customBlue" />
							<span className="font-medium">Duration:</span>
							<span className="ml-1">
								{convertMinutesToHoursAndMinutesString(patient.length)}
							</span>
						</div>
					</div>
				</div>

				<div className="ml-4">
					<div className="text-center">
						<div
							className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold border-2 ${getAcuityColor(patient.acuity)}`}
						>
							{patient.acuity}
						</div>
						<span className="text-xs text-gray-500 mt-1 block">Acuity</span>
					</div>
				</div>
			</div>
		</div>
	)
}

/**
 * A component that displays an error that occurred while calculating reassignments

 *
 * @param Props
 * @param {{message: string, patientId: number| null, type: "addOnError" | "removedPatientsError"}} props.error
 * @param {Function} props.onClose - The function to call when the popup is closed
 * @param {Patient[]} props.unassignedPatientsForAddOnPatients - The list of unassigned patients for add on patients
 */
const ReassignmentsErrorPopup = ({
	error,
	onClose,
	unassignedPatientsForAddOnPatients,
	setNotification,
	logging
}) => {
	const currentSchedule = useSelector(state => state.schedule)
	const permissions = usePermissionsList()
	const isEditingPatient = useSelector(state => state.schedule.isEditing)

	const posthog = usePostHog()

	const [open, setOpen] = useState(false)
	const [isEditOpen, setIsEditOpen] = useState(false)
	const [taskToEdit, setTaskToEdit] = useState(null)
	const [showScrollHint, setShowScrollHint] = useState(false)
	const scheduleSelection = getScheduleSelection(false, false)
	const scrollContainerRef = useRef(null)

	useEffect(() => {
		// Automatically open when error changes and is not null
		if (error) {
			setOpen(true)
			posthog.capture("ReassignmentsErrorPopup", {
				error: error.message,
				patientId: error.patientId
			})
		} else {
			setOpen(false)
		}
		// `posthog` is initialized once at module load and remains stable.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error])

	// Check if content is scrollable
	useEffect(() => {
		const checkScrollable = () => {
			if (scrollContainerRef.current) {
				const container = scrollContainerRef.current
				const isScrollable = container.scrollHeight > container.clientHeight
				setShowScrollHint(isScrollable)
			}
		}

		if (open && unassignedPatientsForAddOnPatients?.length) {
			// Check multiple times as content may still be rendering
			const timeouts = [
				setTimeout(checkScrollable, 50),
				setTimeout(checkScrollable, 200),
				setTimeout(checkScrollable, 500)
			]

			// Also check on window resize
			window.addEventListener("resize", checkScrollable)

			return () => {
				timeouts.forEach(timeout => clearTimeout(timeout))
				window.removeEventListener("resize", checkScrollable)
			}
		}
	}, [unassignedPatientsForAddOnPatients, open])

	const errorType = error?.type

	const openEditPopup = task => {
		if (isEditingPatient) {
		} else {
			logging.info("Opened Edit Patient Popup", {
				task,
				nurse: currentSchedule.nurseSchedule.find(
					nurse => nurse.nurseId === task.assignedNurse
				)
			})
			setTaskToEdit(task)
			setIsEditOpen(true)
		}
	}

	const closeEditPopup = () => {
		setIsEditOpen(false)
		setTaskToEdit(null)
	}

	const handleClose = () => {
		// Check if all patients have been assigned
		if (unassignedPatientsForAddOnPatients && unassignedPatientsForAddOnPatients.length > 0) {
			setNotification({
				visible: true,
				message: "You must manually assign all patients before closing this dialog.",
				success: false
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} else {
			setOpen(false)
			onClose()
		}
	}

	return (
		<>
			<Popup
				open={open}
				onClose={handleClose}
				modal
				nested
				closeOnDocumentClick={false}
				contentStyle={{
					width: "90%",
					maxWidth: "600px",
					border: "none",
					borderRadius: "16px",
					padding: "0"
				}}
				overlayStyle={{ background: "rgba(0, 0, 0, 0.6)" }}
			>
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
					{/* Header */}
					<div className="bg-customBlue p-6 text-white">
						<div className="flex items-center justify-center mb-2">
							<h2 className="text-2xl font-bold text-center">
								Manual Assignment Required
							</h2>
						</div>
						<p className="text-center text-customBlue-light text-sm">
							{errorType === "addOnError"
								? "New Add-On Patients Need Assignment"
								: "Reassignment Due to Cancellations/No Shows"}
						</p>
					</div>

					{/* Instructions */}
					<div className="p-6 bg-customBg border-l-4 border-customBlue">
						<div>
							<h3 className="font-semibold text-customBlue-dark mb-1">What to do:</h3>
							<p className="text-customBlue text-sm">
								The system couldn't automatically assign these patients. Click on
								each patient card below to manually assign them to a nurse and
								chair.
							</p>
						</div>
					</div>

					{/* Patient List */}
					<div className="p-6">
						<h3 className="font-semibold text-gray-800 mb-4 flex items-center">
							<FaUser className="mr-2 text-customBlue" />
							Patients Awaiting Assignment (
							{unassignedPatientsForAddOnPatients?.length || 0})
						</h3>

						{unassignedPatientsForAddOnPatients && (
							<div className="relative">
								<div
									ref={scrollContainerRef}
									className="max-h-[400px] overflow-y-auto pr-2 space-y-1"
								>
									{unassignedPatientsForAddOnPatients.map(patient => (
										<UnassignedPatient
											key={patient.patientId}
											patient={patient}
											openEditPopup={openEditPopup}
										/>
									))}
								</div>

								{showScrollHint && (
									<div className="text-center mt-3 py-2 bg-gray-50 rounded-lg border">
										<div className="flex items-center justify-center text-xs text-gray-500">
											<FaChevronDown className="mr-2 text-customBlue animate-bounce" />
											<span>Scroll for full patient list</span>
										</div>
									</div>
								)}
							</div>
						)}

						{!unassignedPatientsForAddOnPatients?.length && (
							<div className="text-center py-8 text-gray-500">
								<FaUser className="text-4xl mx-auto mb-2 opacity-30" />
								<p>No unassigned patients</p>
							</div>
						)}
					</div>
				</div>
			</Popup>

			{isEditOpen && taskToEdit && (
				<EditPatient
					patient={taskToEdit}
					isOpen
					schedule={currentSchedule}
					onClose={closeEditPopup}
					scheduleSelection={scheduleSelection}
					permissions={permissions}
					setNotification={setNotification}
				/>
			)}
		</>
	)
}

export default ReassignmentsErrorPopup
